import { ArrowPathIcon, ClockIcon, MinusIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon, PauseIcon, PlayIcon } from "@heroicons/react/24/solid";
import { PlacementId } from "@sp-crm/core";
import { useQueryClient } from "@tanstack/react-query";
import { DangerCompactButton } from "components/ui/danger-button";
import { fancyAlert } from "components/ui/fancy-alert";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { PrimaryCompactButton } from "components/ui/primary-button";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { TextArea } from "components/ui/textarea";
import { formatElapsed, useTimer } from "contexts/timer-context";
import { useInvalidateEntityNotes } from "hooks/use-invalidate-entity-notes";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useRouteMatch } from "react-router";
import { Link } from "react-router-dom";
import { useFeature } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { ApplicationState } from "store/state";
import { NO_NAME } from "util/text";
import { ManualTimeEntryForm } from "../clients/show-client/manual-time-entry-form";
import { TrayTimeEntriesSummary } from "./tray-time-entries-summary";

export const TimerTrayItem: React.FC = () => {
    const trackTimeFeature = useFeature("trackTime");
    const { settings } = useTenantSettings();

    if (!trackTimeFeature || !settings.trackTimeEnabled) {
        return null;
    }

    return <TimerTrayItemInner />;
};

interface ClientRouteParams {
    clientId: string;
}

function useCurrentClient(): { clientId: PlacementId; clientName: string } | null {
    const match = useRouteMatch<ClientRouteParams>("/clients/show/:clientId");
    const placementId = (match?.params.clientId ?? null) as PlacementId | null;
    const clientName = useSelector((state: ApplicationState): string | null => {
        if (!placementId) return null;
        const raw = state.clients.clients[placementId];
        if (!raw) return null;
        const preferred = raw.preferredClientName;
        if (typeof preferred === "string" && preferred.length > 0) {
            return preferred;
        }
        const name = raw.name;
        if (typeof name === "string" && name.length > 0) {
            return name;
        }
        return null;
    });
    if (!placementId) return null;
    return { clientId: placementId, clientName: clientName ?? NO_NAME };
}

const TimerTrayItemInner: React.FC = () => {
    const {
        timerState,
        elapsedSeconds,
        clientId,
        clientName,
        pendingAction,
        noteDraft,
        startTimer,
        pauseTimer,
        resumeTimer,
        resetTimer,
        saveEntry,
        setNoteDraft,
    } = useTimer();
    const isBusy = pendingAction !== null;
    const [saving, setSaving] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoggingManually, setIsLoggingManually] = useState(false);
    const [justSaved, setJustSaved] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const justSavedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const wasActiveRef = useRef(timerState !== "idle");

    useEffect(() => {
        return () => {
            if (justSavedTimeoutRef.current) {
                clearTimeout(justSavedTimeoutRef.current);
            }
        };
    }, []);

    const isActive = timerState !== "idle";
    const currentRouteClient = useCurrentClient();

    useEffect(() => {
        if (isActive && !wasActiveRef.current) {
            setIsOpen(true);
        }
        if (!isActive) {
            setIsOpen(false);
            setIsLoggingManually(false);
        }
        wasActiveRef.current = isActive;
    }, [isActive]);

    useEffect(() => {
        if (!isOpen || isActive) {
            return undefined;
        }
        const handler = (event: MouseEvent) => {
            const target = event.target as HTMLElement | null;
            if (
                containerRef.current &&
                !containerRef.current.contains(target) &&
                !target?.closest('[role="dialog"]')
            ) {
                setIsOpen(false);
                setIsLoggingManually(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [isOpen, isActive]);

    const handleSave = useCallback(async () => {
        if (!noteDraft.trim()) return;
        setSaving(true);
        try {
            await saveEntry();
            setIsOpen(false);
            setJustSaved(true);
            if (justSavedTimeoutRef.current) {
                clearTimeout(justSavedTimeoutRef.current);
            }
            justSavedTimeoutRef.current = setTimeout(() => {
                setJustSaved(false);
                justSavedTimeoutRef.current = null;
            }, 1800);
        } catch {
            await fancyAlert("Error", "Failed to save time entry. Please try again.");
        } finally {
            setSaving(false);
        }
    }, [noteDraft, saveEntry]);

    const handleDiscard = useCallback(async () => {
        const confirmed = await fancyConfirm(
            "Discard timer?",
            `Discard the timer for ${clientName} (${formatElapsed(elapsedSeconds)}) without saving?`,
            "Discard",
            "Keep tracking",
        );
        if (!confirmed) return;
        resetTimer();
        setIsOpen(false);
    }, [resetTimer, clientName, elapsedSeconds]);

    const handleMinimize = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleToggle = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    const handleStartForCurrentClient = useCallback(async () => {
        if (!currentRouteClient) return;
        if (isActive && clientId && clientId !== currentRouteClient.clientId) {
            const confirmed = await fancyConfirm(
                "Timer already running",
                `You have a timer running for ${clientName} (${formatElapsed(elapsedSeconds)}). Starting a new timer will discard it.`,
                "Discard & start",
                "Cancel",
            );
            if (!confirmed) return;
        }
        await startTimer(currentRouteClient.clientId, currentRouteClient.clientName);
    }, [currentRouteClient, isActive, clientId, clientName, elapsedSeconds, startTimer]);

    const queryClient = useQueryClient();
    const invalidateNotes = useInvalidateEntityNotes();
    const handleManualSaved = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: [["timeEntries"]] });
        invalidateNotes();
        setIsLoggingManually(false);
        setIsOpen(false);
    }, [queryClient, invalidateNotes]);

    const badgeColor =
        timerState === "running"
            ? "bg-green-500"
            : timerState === "paused"
              ? "bg-amber-500"
              : "";

    const elapsedColor =
        timerState === "running"
            ? "text-green-600"
            : timerState === "paused"
              ? "text-amber-600"
              : "text-gray-600";

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                onClick={handleToggle}
                aria-label={
                    justSaved ? "Time entry saved" : isActive ? "Open timer" : "Timer"
                }
                title={isActive && !isOpen ? "Show timer" : undefined}
                className="relative flex items-center gap-1.5 p-1.5 rounded-lg hover:bg-zinc-950/5 dark:hover:bg-white/5 focus:outline-none">
                {justSaved ? (
                    <span className="relative inline-flex size-5">
                        <span className="absolute inset-0 inline-flex animate-ping rounded-full bg-green-400 opacity-75" />
                        <CheckCircleIcon className="relative size-5 text-green-600" />
                    </span>
                ) : (
                    <ClockIcon className="size-5 text-zinc-500" />
                )}
                {isActive && (
                    <>
                        <span
                            className={`text-xs font-semibold tracking-tight [font-variant-numeric:tabular-nums] ${elapsedColor}`}>
                            {formatElapsed(elapsedSeconds)}
                        </span>
                        <span
                            className={`absolute top-0.5 left-[18px] size-2 rounded-full ${badgeColor} ${timerState === "running" ? "animate-pulse" : ""}`}
                        />
                    </>
                )}
            </button>
            {isOpen && (
                <div className="absolute right-0 z-[1400] mt-2 w-96 max-w-[calc(100vw-2rem)] rounded-lg bg-white shadow-lg ring-1 ring-zinc-950/5 dark:bg-zinc-900 dark:ring-white/10">
                    <div className="flex items-start justify-between px-4 pt-3">
                        <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                            {isActive ? "Timer" : "Time tracker"}
                        </div>
                        {isActive && (
                            <button
                                type="button"
                                onClick={handleMinimize}
                                aria-label="Minimize timer"
                                className="-mt-1 -mr-2 flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-zinc-500 hover:bg-zinc-950/5 hover:text-zinc-800 dark:hover:bg-white/5 dark:hover:text-zinc-200 focus:outline-none">
                                <MinusIcon className="size-4" />
                                Minimize
                            </button>
                        )}
                    </div>
                    <div className="px-4 pb-4 pt-2">
                        {timerState === "idle" && !isLoggingManually && (
                            <IdleTrayContent
                                currentClient={currentRouteClient}
                                onStartForCurrentClient={handleStartForCurrentClient}
                                onLogManualEntry={() => setIsLoggingManually(true)}
                                onNavigate={handleMinimize}
                                pendingAction={pendingAction}
                            />
                        )}
                        {timerState === "idle" &&
                            isLoggingManually &&
                            currentRouteClient && (
                                <div className="space-y-3">
                                    <div className="text-sm text-zinc-700 dark:text-zinc-300">
                                        Add manual entry for{" "}
                                        {currentRouteClient.clientName}
                                    </div>
                                    <ManualTimeEntryForm
                                        clientId={currentRouteClient.clientId}
                                        onSaved={handleManualSaved}
                                        onCancel={() => setIsLoggingManually(false)}
                                        compact
                                    />
                                </div>
                            )}
                        {isActive && (
                            <div className="space-y-3">
                                <div>
                                    <Link
                                        to={`/clients/show/${clientId}`}
                                        onClick={handleMinimize}
                                        className="text-sm font-medium text-brand-600 hover:text-brand-800 truncate block">
                                        {clientName}
                                    </Link>
                                    <div
                                        className={`text-2xl font-semibold tracking-tight [font-variant-numeric:tabular-nums] ${elapsedColor}`}>
                                        {formatElapsed(elapsedSeconds)}
                                    </div>
                                </div>
                                <TextArea
                                    label="Notes"
                                    textSize="sm"
                                    rows={3}
                                    value={noteDraft}
                                    onChange={e => setNoteDraft(e.target.value)}
                                    placeholder="What was done..."
                                />
                                <div className="flex flex-wrap items-center gap-2">
                                    <PrimaryCompactButton
                                        className="items-center"
                                        onClick={handleSave}
                                        disabled={!noteDraft.trim() || saving || isBusy}>
                                        {saving ? "Saving..." : "Stop & save"}
                                    </PrimaryCompactButton>
                                    {timerState === "running" && (
                                        <SecondaryCompactButton
                                            className="items-center"
                                            onClick={pauseTimer}
                                            disabled={isBusy}>
                                            {pendingAction === "pause" ? (
                                                <ArrowPathIcon className="h-3.5 w-3.5 mr-1 animate-spin" />
                                            ) : (
                                                <PauseIcon className="h-3.5 w-3.5 mr-1" />
                                            )}
                                            Pause
                                        </SecondaryCompactButton>
                                    )}
                                    {timerState === "paused" && (
                                        <SecondaryCompactButton
                                            className="items-center"
                                            onClick={resumeTimer}
                                            disabled={isBusy}>
                                            {pendingAction === "resume" ? (
                                                <ArrowPathIcon className="h-3.5 w-3.5 mr-1 animate-spin" />
                                            ) : (
                                                <PlayIcon className="h-3.5 w-3.5 mr-1" />
                                            )}
                                            Resume
                                        </SecondaryCompactButton>
                                    )}
                                    <DangerCompactButton
                                        className="items-center ml-auto"
                                        onClick={handleDiscard}
                                        disabled={isBusy || saving}>
                                        Discard
                                    </DangerCompactButton>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

interface IdleTrayContentProps {
    currentClient: { clientId: PlacementId; clientName: string } | null;
    onStartForCurrentClient: () => void;
    onLogManualEntry: () => void;
    onNavigate: () => void;
    pendingAction: "start" | "pause" | "resume" | null;
}

const IdleTrayContent: React.FC<IdleTrayContentProps> = ({
    currentClient,
    onStartForCurrentClient,
    onLogManualEntry,
    onNavigate,
    pendingAction,
}) => {
    if (!currentClient) {
        return (
            <div className="space-y-2">
                <p className="text-sm text-zinc-600 dark:text-zinc-300">
                    <Link
                        to="/pipeline"
                        onClick={onNavigate}
                        className="font-medium text-brand-600 hover:text-brand-800">
                        Open a client
                    </Link>{" "}
                    to start a timer or log time.
                </p>
                <p className="text-sm text-zinc-600 dark:text-zinc-300">
                    Or{" "}
                    <Link
                        to="/reports/show/time-tracking"
                        onClick={onNavigate}
                        className="font-medium text-brand-600 hover:text-brand-800">
                        view the time tracking report
                    </Link>
                    .
                </p>
            </div>
        );
    }
    return (
        <div className="space-y-3">
            <p className="text-sm text-zinc-700 dark:text-zinc-300">
                Track time for{" "}
                <span className="font-medium">{currentClient.clientName}</span>
            </p>
            <div className="flex gap-2">
                <PrimaryCompactButton
                    className="items-center"
                    onClick={onStartForCurrentClient}
                    disabled={pendingAction === "start"}>
                    {pendingAction === "start" ? (
                        <ArrowPathIcon className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                        <PlayIcon className="h-3.5 w-3.5 mr-1" />
                    )}
                    Start timer
                </PrimaryCompactButton>
                <SecondaryCompactButton onClick={onLogManualEntry}>
                    Add manual entry
                </SecondaryCompactButton>
            </div>
            <TrayTimeEntriesSummary
                clientId={currentClient.clientId}
                clientName={currentClient.clientName}
                onNavigate={onNavigate}
            />
        </div>
    );
};
